import { ENROLLMENT_STATE_LABELS } from '@repo/types';
import type { EnrollmentListItemDto } from '@repo/types';
import type { EnrollmentKind, EnrollmentState } from '@theailab-dev/parchment';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CustomError, Empty, EnrollmentGrantModal } from '../components';
import { getEnrollmentStateBadgeVariant, getKindLabel } from '../utils';
import { TablePageLayout } from '@/components/Layout/TablePageLayout';
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  DateRangeFilter,
  LoadingSpinner,
  SelectFilter,
} from '@/components/ui';
import { useEnrollments } from '@/hooks/useEnrollments';
import { formatDateTimeKoreanLocale } from '@/utils/dateTime';

/**
 * Enrollments 컴포넌트 - 수강권 목록 페이지
 * 단체 유료특강 수강권 부여 기능 주석 처리
 */
function Enrollments() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentLimit = parseInt(searchParams.get('limit') || '20', 10);

  // 필터 파라미터
  const filterType = (searchParams.get('filterType') as 'course' | 'product') || undefined; // 'product' or 'course'
  const search = searchParams.get('search') || '';
  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';

  // 로컬 검색 상태 (검색 버튼 클릭 전까지 URL에 반영하지 않음)
  const [searchQuery, setSearchQuery] = useState(search);

  // URL searchParams가 변경되면 로컬 상태도 동기화
  useEffect(() => {
    setSearchQuery(search);
  }, [search]);

  const { data, isLoading, isError, error, refetch } = useEnrollments({
    page: currentPage,
    limit: currentLimit,
    filterType: (filterType as 'course' | 'product') || undefined,
    search: search || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  useEffect(() => {
    if (!searchParams.has('page') || !searchParams.has('limit')) {
      setSearchParams({ page: '1', limit: '20' }, { replace: true });
    }
  }, []);

  const meta = data?.meta;
  const page = meta?.page || currentPage;
  const limit = meta?.limit || currentLimit;
  const total = meta?.total || 0;
  const totalPages = meta?.totalPages || 1;
  const enrollments: EnrollmentListItemDto[] = data?.data || [];

  const handlePageChange = (nextPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', nextPage.toString());
    setSearchParams(params);
  };

  const handleLimitChange = (newLimit: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('limit', newLimit.toString());
    params.set('page', '1');
    setSearchParams(params);
  };

  // 필터 핸들러
  const handleFilterTypeChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (value) {
      params.set('filterType', value);
    } else {
      params.delete('filterType');
    }
    setSearchParams(params);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSearch = () => {
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (searchQuery.trim()) {
      params.set('search', searchQuery.trim());
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleDateChange = (type: 'startDate' | 'endDate', value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (value) {
      params.set(type, value);
    } else {
      params.delete(type);
    }
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setSearchParams({ page: '1', limit: currentLimit.toString() });
  };

  const [isGrantModalOpen, setIsGrantModalOpen] = useState<boolean>(false);

  const handleGrantLectureModalOpen = async () => {
    setIsGrantModalOpen(true);
  };

  // const canGrantPremiumLecture = filterType && search.trim().length > 0 && startDate.trim().length > 0;
  // const handleGrantPremiumLecture = async () => {
  //   setIsGrantModalOpen(true);
  // };

  if (isError) {
    return <CustomError error={error} refetch={refetch} />;
  }

  return (
    <TablePageLayout
      title="수강권 목록"
      description="전체 수강권을 관리하고 모니터링하세요"
      isLoading={isLoading}
      currentPage={page}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      totalItems={total}
      limit={limit}
      onLimitChange={handleLimitChange}
      filters={
        <div className="flex flex-wrap items-center gap-4">
          <SelectFilter
            value={filterType}
            onChange={handleFilterTypeChange}
            options={[
              { value: 'product', label: '상품' },
              { value: 'course', label: '코스' },
            ]}
            label="타입"
            placeholder="전체"
            id="filterTypeSelect"
          />
          <div className="flex items-center gap-2">
            <label htmlFor="searchInput" className="text-sm whitespace-nowrap text-slate-500">
              검색
            </label>
            <input
              id="searchInput"
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={
                filterType === 'product' ? '상품 ID/Title' : filterType === 'course' ? '코스 ID/Title' : 'ID/Title'
              }
              className="min-w-[200px] rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 transition-colors hover:border-slate-300 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
            <Button variant="primary" size="sm" onClick={handleSearch}>
              검색
            </Button>
          </div>
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={(value) => handleDateChange('startDate', value)}
            onEndDateChange={(value) => handleDateChange('endDate', value)}
            label="수강 신청일"
            startDateId="enrollmentStartDate"
            endDateId="enrollmentEndDate"
          />
          <Button variant="outline" size="sm" onClick={handleResetFilters} className="ml-auto">
            초기화
          </Button>
        </div>
      }
      search={
        <button
          className="ml-auto flex rounded-lg bg-green-700 px-4 py-2 text-sm text-white transition-colors hover:bg-green-800 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 disabled:hover:bg-slate-300"
          onClick={handleGrantLectureModalOpen}>
          수강권 부여
        </button>
        // <button
        //   disabled={!canGrantPremiumLecture}
        //   className="ml-auto flex rounded-lg bg-green-700 px-4 py-2 text-sm text-white transition-colors hover:bg-green-800 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 disabled:hover:bg-slate-300"
        //   onClick={handleGrantPremiumLecture}>
        //   유료특강 수강권 부여
        // </button>
      }>
      {isLoading ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24 whitespace-nowrap">ID</TableHead>
                <TableHead className="min-w-[240px]">코스</TableHead>
                <TableHead className="min-w-[200px]">상품</TableHead>
                <TableHead className="whitespace-nowrap">학습자</TableHead>
                <TableHead className="whitespace-nowrap">수강권 유형</TableHead>
                <TableHead className="whitespace-nowrap">학습 진도</TableHead>
                <TableHead className="whitespace-nowrap">상태</TableHead>
                <TableHead className="whitespace-nowrap">결제일(등록일)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {enrollments.map((enrollment) => (
                <TableRow
                  key={enrollment.id}
                  onClick={() => navigate(`/dashboard/user/${enrollment.account.id}/enrollment/${enrollment.id}`)}>
                  <TableCell nowrap className="text-slate-500">
                    #{enrollment.id}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium text-slate-900">{enrollment.course.title}</div>
                      <div className="text-sm text-slate-500">#{enrollment.course.id}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {enrollment.product ? (
                      <div className="space-y-1">
                        <div className="font-medium text-slate-900">{enrollment.product.title}</div>
                        {enrollment.product.subtitle && (
                          <div className="text-sm text-slate-500">{enrollment.product.subtitle}</div>
                        )}
                        <div className="text-sm text-slate-500">#{enrollment.product.id}</div>
                      </div>
                    ) : (
                      <div className="text-sm text-slate-400">-</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium text-slate-900">{enrollment.account.name}</div>
                      <div className="text-sm text-slate-500">{enrollment.account.email}</div>
                      <div className="text-sm text-slate-500">{enrollment.account.phone}</div>
                    </div>
                  </TableCell>
                  <TableCell nowrap>{getKindLabel(enrollment.kind as EnrollmentKind)}</TableCell>
                  <TableCell nowrap className="text-slate-600">
                    {`${enrollment.completedLessonCount}/${enrollment.totalLessonCount}개 진행(${Math.round((enrollment.completedLessonCount / enrollment.totalLessonCount) * 100)}%)`}
                  </TableCell>
                  <TableCell nowrap>
                    <Badge variant={getEnrollmentStateBadgeVariant(enrollment.state as EnrollmentState)}>
                      {ENROLLMENT_STATE_LABELS[enrollment.state as EnrollmentState] ||
                        (enrollment.state as EnrollmentState)}
                    </Badge>
                  </TableCell>
                  <TableCell nowrap className="text-slate-500">
                    {formatDateTimeKoreanLocale(enrollment.createdAt as Date | string | null)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!isLoading && enrollments.length === 0 && <Empty />}
        </>
      )}

      {/* 유료특강 수강권 부여 모달 */}
      <EnrollmentGrantModal isOpen={isGrantModalOpen} onClose={() => setIsGrantModalOpen(false)} />
      {/* <SpecialProductEnrollmentsGrantModal
        isOpen={isGrantModalOpen}
        filterType={filterType as 'course' | 'product'}
        search={search}
        startDate={startDate}
        endDate={endDate}
        onClose={() => setIsGrantModalOpen(false)}
      /> */}
    </TablePageLayout>
  );
}

export default Enrollments;
